import { useFlowTools } from 'flowTools/useFlowTools';
import React, { useContext } from 'react';
import { useItineraryTripActions } from '../ItineraryTripProvider';
import useItineraryTracking from './useItineraryTracking';
import { useItinerarySelection } from '../ItinerarySelectionProvider';

import useOdiItineraryTracking from '../../hooks/useOdiItineraryTracking';
import { useFeeType } from 'clientState/providers/FeeTypeProvider';
import { useShoppingCartActions } from './ShoppingCartRequestHooks';
import {
  useItinerary,
  useItineraryPosition,
} from '@components/Results/Components/Itinerary/Provider';
import {
  useHotelVoucherItineraryIds,
  useSearchId,
  useFlightOfTheDayItineraryIds,
} from 'clientState/providers/SearchResponseProvider';
import { useItineraryFeeTypeIdSelection } from 'clientState/providers/ItinerarySelectionProvider/ItineraryFeeTypeProvider';
import { ItinerarySelectionActionsContext } from './ItinerarySelectionActionsContext';
import {
  useIsAlternativeItineraryDesktopEnabled,
  useAlternativeItineraryLayerActions,
} from '../AlternativeItineraryProvider/AlternativeItineraryLayerProvider';
import { useAlternativeItineraryActions } from 'clientState/providers/AlternativeItineraryProvider/AlternativeItineraryCalculatorProvider';
import useAlternativeFlight from 'clientState/hooks/useAlternativeFlight';
import { HighlightedType } from '@components/Results/Components/ItineraryHeader/constants/highlightedTypes';

const getFee = (itinerary, feeTypeId) =>
  itinerary.fees.find(({ type }) => type.id === feeTypeId);

export const getSelectedItinerary = (itinerary, feeTypeId) => {
  if (!feeTypeId) {
    return itinerary;
  }
  const fee = getFee(itinerary, feeTypeId);
  return { ...itinerary, price: fee.price, feeType: fee.type };
};

const ActionsProvider = ({ actions, children }) => {
  return (
    <ItinerarySelectionActionsContext.Provider value={actions}>
      {children}
    </ItinerarySelectionActionsContext.Provider>
  );
};

export const useGoToDesktopPassengersPage = () => {
  const itinerary = useItinerary();
  const { id } = itinerary;
  const position = useItineraryPosition();
  const { selectItineraryAndContinue } = useShoppingCartActions();
  const sendTracking = useItineraryTracking();
  const trackOdiRating = useOdiItineraryTracking();
  const feeType = useFeeType();
  const isAlternativeFlightDesktopEnabled =
    useIsAlternativeItineraryDesktopEnabled();
  const { open: openAlternativeItineraryLayer, setFeeType } =
    useAlternativeItineraryLayerActions();
  const { requestAlternativeItinerary, hasAlternativeItineraryCarrier } =
    useAlternativeItineraryActions();
  const { setAlternativeFlight } = useAlternativeFlight();
  const flightOfTheDayItineraryIds = useFlightOfTheDayItineraryIds();
  const isFlightOfTheDay =
    itinerary.highlightedType !== HighlightedType.Recommended &&
    flightOfTheDayItineraryIds.includes(id);

  const goToDesktopPassengersPage = (request = {}) => {
    const alternativeFlight = {
      originalItineraryId: itinerary?.id,
      selectedItinerary: itinerary,
      selectedFeeType: request.feeTypeId,
    };
    setAlternativeFlight(alternativeFlight);
    if (
      isAlternativeFlightDesktopEnabled &&
      hasAlternativeItineraryCarrier(itinerary)
    ) {
      setFeeType(request.feeTypeId);
      openAlternativeItineraryLayer();
      requestAlternativeItinerary(itinerary);
    } else {
      const selectedItinerary = getSelectedItinerary(
        itinerary,
        request.feeTypeId
      );
      selectItineraryAndContinue(itinerary, {
        feeTypeId: feeType.id,
        ...request,
      });
      if (isFlightOfTheDay) {
        sendTracking(itinerary, position);
      } else {
        sendTracking(selectedItinerary, position);
      }
      trackOdiRating(selectedItinerary);
    }
  };
  return goToDesktopPassengersPage;
};

export const useItinerarySelectionActions = () => {
  const itinerary = useItinerary();
  const { isDesktop } = useFlowTools();
  const { open = () => {} } = useItineraryTripActions() || {};
  const setItineraryInfo = useItinerarySelection();
  const sendTracking = useItineraryTracking();
  const feeType = useFeeType();
  const selectItineraryFeeTypeId = useItineraryFeeTypeIdSelection();
  const trackOdiRating = useOdiItineraryTracking();
  const actions = useContext(ItinerarySelectionActionsContext);
  const searchId = useSearchId();
  const hotelVoucherItineraryIds = useHotelVoucherItineraryIds();
  const flightOfTheDayItineraryIds = useFlightOfTheDayItineraryIds();
  const isFlightOfTheDay = flightOfTheDayItineraryIds.includes(itinerary?.id);

  const showItineraryTripDesktop = (itinerary) => {
    open();
    setItineraryInfo({ itinerary });
  };

  const showItineraryTripMobile = (itinerary, position, request = {}) => {
    const feeTypeId = request.feeTypeId ?? feeType.id;
    const selectedItinerary = getSelectedItinerary(itinerary, feeTypeId);
    const isHotelVoucher = hotelVoucherItineraryIds.includes(itinerary.id);
    const isPrimeDayFare =
      itinerary?.campaignConfig?.primeDayConfig?.isPrimeDayFare &&
      itinerary?.campaignConfig?.primeDayConfig?.primeDayFareType &&
      itinerary?.campaignConfig?.primeDayConfig?.primeDayFareType !== 'BRONZE';

    const segmentKeys = itinerary.legs.reduce((acc, actual) => {
      return acc.concat(actual.segmentKeys);
    }, []);

    const flightSummaryParams = {
      searchId,
      fareItineraryKey: itinerary.key,
      segmentKeys,
      carbonFootprint:
        itinerary?.carbonFootprint?.ecoPercentageThanAverage || 0,
      feeTypeId: request.feeTypeId || feeType?.[0]?.id,
      highlightOffer: !isPrimeDayFare && isHotelVoucher,
    };
    const base64Params = btoa(JSON.stringify(flightSummaryParams));

    open(base64Params);
    setItineraryInfo({
      itinerary: selectedItinerary,
      position,
    });
    selectItineraryFeeTypeId(feeTypeId);
    if (isFlightOfTheDay) {
      sendTracking(itinerary, position);
    } else {
      sendTracking(selectedItinerary, position, isHotelVoucher);
    }
    trackOdiRating(selectedItinerary);
  };

  return {
    showItineraryTrip: isDesktop
      ? showItineraryTripDesktop
      : showItineraryTripMobile,
    ...actions,
  };
};
export const useItineraryActions = () => {
  const itinerary = useItinerary();
  const position = useItineraryPosition();
  const { showItineraryTrip = () => {} } = useItinerarySelectionActions();

  return {
    showItineraryTrip: (props = {}) => {
      return showItineraryTrip(itinerary, position, props);
    },
  };
};

export default ActionsProvider;
